import React from 'react';
import { Users, House, Wallet, Check, X, Tag, Clock, Eye, TrendUp } from '@phosphor-icons/react';
import { getCurrencySymbol, getSymbol } from '../../utils/currency';
import { StatCard, MiniStat, DarkCard, StatusBadge } from './_shared';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const VisitTrend = ({ trend = [] }) => {
  const max = Math.max(1, ...trend.map(d => d.count));
  return (
    <div className="bg-[#161b26] border border-[#252d3d] rounded-2xl p-5" data-testid="visit-trend-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <TrendUp size={16} className="text-indigo-400" weight="bold" />
          <h3 className="text-white text-sm font-semibold">Visits — Last 7 Days</h3>
        </div>
        <span className="text-[#6b7a99] text-xs">{trend.reduce((s, d) => s + d.count, 0)} total</span>
      </div>
      <div className="flex items-end justify-between gap-2 h-28">
        {trend.map((d) => {
          const label = DAY_LABELS[new Date(d.date + 'T00:00:00Z').getUTCDay()];
          const h = Math.round((d.count / max) * 100);
          return (
            <div key={d.date} className="flex-1 flex flex-col items-center gap-1.5" data-testid={`trend-bar-${d.date}`}>
              <span className="text-[10px] text-[#8896b0] font-semibold">{d.count}</span>
              <div className="w-full flex items-end justify-center" style={{ height: '72px' }}>
                <div className="w-full max-w-[28px] rounded-t-md bg-gradient-to-t from-indigo-600 to-indigo-400 transition-all"
                  style={{ height: `${Math.max(4, h)}%` }} />
              </div>
              <span className="text-[10px] text-[#6b7a99]">{label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const DashboardView = ({ stats, visitStats, categories, activeCats, pendingListings, listings, bookings, handleApproveListing, handleRejectListing }) => {
  const pendingList = (listings || []).filter(l => l.status === 'pending');
  return (
  <div>
    <p className="text-[#6b7a99] text-sm mb-6">Overview of Look4Space platform activity</p>
    {/* Stat Cards Row 1 */}
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
      <StatCard title="Total Visits" value={(visitStats?.unique_visitors ?? 0).toLocaleString()} sub={`${visitStats?.visits_today || 0} today`} iconBg="bg-indigo-500" icon={Eye} trend={`+${visitStats?.visits_today || 0}`} />
      <StatCard title="Total Users" value={(stats?.total_users || 0).toLocaleString()} sub="registered" iconBg="bg-blue-500" icon={Users} trend={`+${stats?.total_users || 0}`} />
      <StatCard title="Total Listings" value={stats?.total_listings || 0} sub={`${stats?.live_listings || 0} live`} iconBg="bg-emerald-500" icon={House} trend={`+${stats?.live_listings || 0}`} />
      <StatCard title="Total Bookings" value={stats?.total_bookings || 0} sub="all time" iconBg="bg-amber-500" icon={Wallet} trend="+0" />
    </div>
    {/* Visit trend + secondary stats */}
    <div className="grid lg:grid-cols-2 gap-4 mb-8">
      <VisitTrend trend={visitStats?.trend || []} />
      <div className="grid grid-cols-2 gap-4 content-start">
        <MiniStat title="Live Listings" value={stats?.live_listings || 0} iconBg="bg-emerald-500/20" iconColor="text-emerald-400" icon={House} />
        <MiniStat title="Pending Approval" value={pendingListings} iconBg="bg-amber-500/20" iconColor="text-amber-400" icon={Clock} />
        <MiniStat title="Active Categories" value={`${activeCats}/${categories.length}`} iconBg="bg-purple-500/20" iconColor="text-purple-400" icon={Tag} />
        <MiniStat title="Revenue" value={`${getCurrencySymbol()}${(stats?.total_revenue || 0).toLocaleString()}`} iconBg="bg-red-500/20" iconColor="text-red-400" icon={Wallet} />
      </div>
    </div>
    {/* Quick Actions */}
    <div className="grid lg:grid-cols-2 gap-6">
      <DarkCard title="Pending Approval" data-testid="pending-approval-card">
        {pendingList.slice(0, 5).map(l => (
          <div key={l.listing_id} className="flex items-center justify-between py-2.5 border-b border-[#252d3d] last:border-0">
            <div className="flex-1 min-w-0"><p className="text-white text-sm font-medium truncate">{l.title}</p><p className="text-[#6b7a99] text-xs">{l.category}</p></div>
            <div className="flex gap-1.5 ml-3">
              <button onClick={() => handleApproveListing(l.listing_id)} className="w-7 h-7 bg-emerald-500/20 hover:bg-emerald-500/30 rounded-lg flex items-center justify-center" data-testid={`approve-${l.listing_id}`}><Check size={14} className="text-emerald-400" weight="bold" /></button>
              <button onClick={() => handleRejectListing(l.listing_id)} className="w-7 h-7 bg-red-500/20 hover:bg-red-500/30 rounded-lg flex items-center justify-center" data-testid={`reject-${l.listing_id}`}><X size={14} className="text-red-400" weight="bold" /></button>
            </div>
          </div>
        ))}
        {pendingList.length === 0 && <p className="text-[#6b7a99] text-sm text-center py-6">No pending listings</p>}
      </DarkCard>
      <DarkCard title="Recent Bookings">
        {bookings.slice(0, 5).map(b => (
          <div key={b.booking_id} className="flex items-center justify-between py-2.5 border-b border-[#252d3d] last:border-0">
            <div className="flex-1 min-w-0"><p className="text-white text-sm font-medium truncate">{b.listing_title}</p><p className="text-[#6b7a99] text-xs">{b.buyer_name} - {getSymbol(b.currency || b.country)}{b.total_price}</p></div>
            <StatusBadge status={b.status} />
          </div>
        ))}
        {bookings.length === 0 && <p className="text-[#6b7a99] text-sm text-center py-6">No bookings yet</p>}
      </DarkCard>
    </div>
  </div>
  );
};

export default DashboardView;
